import { useMutation } from '@tanstack/react-query';
import { Form, Modal, Space, TableProps } from 'antd';
import dayjs from 'dayjs';
import React, {
  Dispatch,
  SetStateAction,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
} from 'react';

import {
  QUERY_UPDATE_ROUTE_BY_ID,
  VARIABLES_UPDATE_ROUTE,
} from 'apis/GraphQL/Queries/saleLineQuery';
import axiosGraphQL from 'apis/GraphQL/axiosGraphQL';
import SaleLineTeamApis, { IUpdateSaleTeamRoute } from 'apis/SaleLineTeamApis';
import { EditIcon, User3Icon } from 'assets/svg';
import { Switch } from 'components';
import ButtonAction from 'components/Button/ButtonAction';
import TableComponent from 'components/Table/TableComponent';
import { useUser } from 'contexts/UserProvider';
import { IParamQraphQL } from 'interfaces/GraphQL/GraphQL';
import { ISaleLineItemData } from 'interfaces/SaleLine';
import { ColumnsTypeCustom } from 'interfaces/Table/ColumnType';
import Content from 'layouts/Content';
import { HocChangePagination } from 'utils/HocChangePagination';
import {
  convertDateToBE,
  toastError,
  toastSuccess,
} from 'utils/constants/functions';
import { icons } from 'utils/constants/icons';
import { optionStatus } from './ModalSaleLinePage';
import { ISaleLineData } from './SaleLinePage';

interface IProps extends React.PropsWithChildren<TableProps> {
  data: ISaleLineData;
  setDrawerData: Dispatch<any>;
  setEditIndex: Dispatch<SetStateAction<number>>;
  setOpenModal: Dispatch<SetStateAction<boolean>>;
  setOpenDrawer: Dispatch<SetStateAction<boolean>>;
  refetch: (isExport?: boolean, headerRow?: any) => void;
}

const { ExclamationCircleFilled } = icons;

const TableSaleLinePage = forwardRef((props: IProps, ref: any) => {
  const { confirm } = Modal,
    {
      data,
      loading,
      refetch,
      setEditIndex,
      setOpenModal,
      setOpenDrawer,
      setDrawerData,
    } = props;

  const { user } = useUser(),
    [form] = Form.useForm();

  const isStatusActive = useCallback((statusId: string) => {
    return statusId === 'R100_01';
  }, []);

  const mutationEditRoute = useMutation({
      mutationKey: ['editRoute'],
      mutationFn: (params: IParamQraphQL) =>
        axiosGraphQL.post('/graphql', params),
      onSuccess: (res: any) => {
        if (res?.data?.updateRouteM) {
          toastSuccess('Cập nhật tuyến thành công!');
          refetch();
          return;
        }
        if (res?.errors)
          toastError(`Cập nhật tuyến thất bại. ${res?.errors[0]?.message}`);
      },
      onError: (error) => {
        toastError(`Cập nhật tuyến thất bại. ${error?.message}`);
      },
    }),
    mutationCheckExistSaleTeamInRoute = useMutation({
      mutationFn: (record: ISaleLineItemData) =>
        SaleLineTeamApis.checkExistSaleTeamInRoute(record.routeId),
      onSuccess: (res, variables) => {
        if (res?.data?.getTeamSalesRoute) {
          handleClickSwitch({
            ...variables,
            teamSales: res?.data?.getTeamSalesRoute?.items ?? [],
          });
        }
      },
      onError: (error) => {
        console.log(error);
      },
    }),
    {
      mutate: updateManySalesTeamRoute,
      isPending: isUpdateManySalesTeamRoutePending,
    } = useMutation({
      mutationFn: (params: IUpdateSaleTeamRoute[]) =>
        SaleLineTeamApis.updateManySalesTeamRoute(params),
      onSuccess: (res) => {
        if (res) console.log(res);
      },
      onError: (error: any) => {
        console.log(error?.message);
      },
    });

  const handleClickCustomer = (record: ISaleLineItemData) => {
    setDrawerData(record);
    setOpenDrawer((prev) => !prev);
  };

  const handleClickEditRow = (index: number) => {
    form.resetFields();

    setEditIndex(index);
    setOpenModal(true);
  };

  const handleChangeSwitch = (record: ISaleLineItemData) => {
    if (isStatusActive(record.statusId))
      mutationCheckExistSaleTeamInRoute.mutate(record);
    else handleSwitch(record);
  };

  // plant_type = 1: Chi nhánh chỉ xem được data của chi nhánh mình
  // plant_type=0: Hội sở xem được data các chi nhánh mình quản lý
  const columns: ColumnsTypeCustom = useMemo(() => {
    return [
      {
        width: 50,
        title: 'STT',
        align: 'center',
        dataIndex: 'stt',
        className: 'align-top',
        render: (_, __, index) =>
          data.pageInfo?.itemsPerPage * (data.pageInfo?.currentPage - 1) +
          index +
          1,
      },
      {
        width: 120,
        title: 'Mã tuyến',
        dataIndex: 'routeId',
        className: 'align-top',
        render: (value) => value || '-',
      },
      {
        width: 190,
        align: 'left',
        title: 'Tên tuyến',
        className: 'align-top',
        dataIndex: 'routeName',
        // render: (value) => value || '-',
        render: (value) => (
          <p className="w-[180px] overflow-hidden whitespace-nowrap text-ellipsis">
            {value}
          </p>
        ),
      },
      {
        width: 130,
        align: 'center',
        className: 'align-top',
        title: 'Thời gian (ngày)',
        dataIndex: 'numExpectedDays',
        render: (value) => value || '-',
      },
      {
        width: 165,
        className: 'align-top',
        dataIndex: 'cityName',
        title: 'Tỉnh/Thành Phố',
        render: (value) => value || '-',
      },
      {
        width: 165,
        align: 'left',
        title: 'Quận/Huyện',
        className: 'align-top',
        dataIndex: 'districtName',
        render: (value) => value || '-',
      },
      {
        width: 165,
        align: 'left',
        title: 'Phường/Xã',
        className: 'align-top',
        dataIndex: 'wardName',
        render: (value) => value || '-',
      },
      {
        width: 165,
        className: 'align-top',
        key: 'cityNameNew',
        dataIndex: 'area',
        title: 'Tỉnh/Thành Phố (mới)',
        render: (areas: any[]) => {
          if (!areas || areas.length === 0) return '-';
          const cityNames = [
            ...new Set(areas.map((a) => a.cityNameNew).filter(Boolean)),
          ];
          return cityNames.join(', ') || '-';
        },
      },
      {
        width: 165,
        align: 'left',
        title: 'Phường/Xã (mới)',
        className: 'align-top',
        key: 'wardNameNew',
        dataIndex: 'area',
        render: (areas: any[]) => {
          if (!areas || areas.length === 0) return '-';
          const wardNames = areas.map((a) => a.wardNameNew).filter(Boolean);
          return wardNames.join(', ') || '-';
        },
      },
      {
        width: 100,
        title: 'Mã chuyến',
        className: 'align-top',
        dataIndex: 'tripId',
        render: (value) => value || '-',
      },
      {
        width: 150,
        align: 'left',
        title: 'Tên chuyến',
        className: 'align-top',
        dataIndex: 'tripName',
        render: (value) => value || '-',
      },
      {
        width: 170,
        align: 'center',
        className: 'align-top',
        dataIndex: 'indexNum',
        title: 'Thứ tự tuyến/chuyến',
        render: (value) => value || '-',
      },

      {
        width: 90,
        title: 'Chi nhánh',
        className: 'align-top',
        dataIndex: 'plantId',
        render: (value) => value || '-',
      },

      {
        width: 140,
        align: 'center',
        className: 'align-top',
        dataIndex: 'companyId',
        title: 'Công ty phát hành',
        render: (value) => value || '-',
      },
      {
        width: 140,
        title: 'Trạng thái',
        dataIndex: 'statusId',
        className: 'align-top',
        render: (value) => {
          const active = optionStatus.find((option) => option.value === value);
          return active ? active.label : '-';
        },
      },
      {
        width: 170,
        type: 'date',
        isShowRender: true,
        className: 'align-top',
        title: 'Ngày hoạt động',
        dataIndex: 'validFromDate',
        render: (value: any) =>
          value ? dayjs(value).format('DD/MM/YYYY') : '-',
      },
      {
        width: 160,
        type: 'date',
        isShowRender: true,
        title: 'Ngày kết thúc',
        className: 'align-top',
        dataIndex: 'validToDate',
        render: (value: any) =>
          value ? dayjs(value).format('DD/MM/YYYY') : '-',
      },
      {
        width: 160,
        title: 'Người tạo',
        className: 'align-top',
        dataIndex: 'createUser',
        render: (value) => value || '-',
      },
      {
        width: 180,
        title: 'Ngày tạo',
        type: 'date',
        dataIndex: 'createDate',
        className: 'align-top',
        render: (value: any) =>
          value ? dayjs(value).format('DD/MM/YYYY HH:mm:ss') : '-',
      },
      {
        width: 160,
        title: 'Người cập nhật',
        className: 'align-top',
        dataIndex: 'modifyUser',
        render: (value) => value || '-',
      },
      {
        width: 180,
        type: 'date',
        title: 'Ngày cập nhật',
        className: 'align-top',
        dataIndex: 'modifyDate',
        render: (value) =>
          value ? dayjs(value).format('DD/MM/YYYY HH:mm:ss') : '-',
      },
      {
        width: 190,
        title: '',
        fixed: 'right',
        align: 'center',
        className: 'align-top',
        render: (_, record, index) => (
          <Space size={'middle'} className="text-lg ">
            <ButtonAction
              tooltip="Khách hàng"
              onClick={() => handleClickCustomer(record)}
            >
              <User3Icon />
            </ButtonAction>
            <ButtonAction
              tooltip="Chỉnh sửa"
              onClick={() => handleClickEditRow(index)}
            >
              <EditIcon />
            </ButtonAction>
            <Switch
              value={isStatusActive(record.statusId)}
              onChange={() => handleChangeSwitch(record)}
            />
          </Space>
        ),
      },
    ];
  }, [data]);

  useImperativeHandle(ref, () => ({
    handleExportExcel,
  }));

  const handleExportExcel = () => {
    refetch(true, columns);
  };

  const handleClickSwitch = (record: any) => {
    if (typeof user === 'undefined') return;
    try {
      if (isStatusActive(record?.statusId) && record?.teamSales.length > 0) {
        confirm({
          title: `
            Đang có ${record?.teamSales.length} đội
            ${record?.teamSales.map((item: any) => item?.tteamSales.subId).join(', ')}
            trong tuyến ${record?.routeId}. Bạn có muốn ngưng hoạt động tuyến này không?
          `,
          icon: <ExclamationCircleFilled />,
          onOk: () => {
            handleSwitch(record);
            // updateManySalesTeamRoute(
            //   (record?.teamSales ?? []).map((item: any) => ({
            //     id: item?.id ?? '',
            //     useYn: false,
            //   })),
            // );
          },
        });
        return;
      }

      handleSwitch(record);
    } catch (error: any) {
      toastError(error);
    }
  };

  const handleSwitch = (record: ISaleLineItemData) => {
    const statusId = optionStatus.find(
      (option) => option.value !== record.statusId,
    )?.value;

    // const today = dayjs(new Date().toISOString());

    mutationEditRoute.mutate({
      query: QUERY_UPDATE_ROUTE_BY_ID,
      variables: VARIABLES_UPDATE_ROUTE({
        id: record.routeId,
        statusId: statusId,
        // validToDate: isStatusActive(record.statusId) ? today : null,
        validToDate: isStatusActive(record.statusId)
          ? convertDateToBE(new Date())
          : null,
      }),
    });
  };

  return (
    <Content>
      <Form form={form}>
        <TableComponent
          columns={columns}
          dataSource={data.items}
          rowKey={(record: any) => record.routeId}
          loading={
            loading ||
            mutationCheckExistSaleTeamInRoute.isPending ||
            isUpdateManySalesTeamRoutePending
          }
          pagination={{
            current: data.pageInfo?.currentPage,
            pageSize: data.pageInfo?.itemsPerPage,
            total: data.pageInfo?.totalItems,
            onChange: HocChangePagination(),
          }}
        />
      </Form>
    </Content>
  );
});

export default TableSaleLinePage;
